import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  FormControl,
  Collapse,
} from "@mui/material";
import Checkbox from "components/common/Checkbox";
import LinkRouter from "components/navigation/LinkRouter";
import { ExternalLink, LockKeyhole } from "lucide-react";
import { useAdvisorProfilePath } from "components/common/CurrentUserAvatar";
import Breadcrumbs from "components/common/Breadcrumbs";
import useIsMobile from "components/hooks/useIsMobile";
import Loading from "components/common/Loading";
import { DelegationTask } from "protogen/delegations_pb";
import { UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import DateDisplay from "components/common/DateDisplay";
import {
  Clock,
  CheckCircle2,
  Info,
  Zap,
  CalendarDays,
  Copy,
  ChevronDown,
  Pencil,
} from "lucide-react";
import EditDelegationDetailsDialog from "components/delegation/EditDelegationDetailsDialog";
import {
  parseDetails,
  separateDetailsByType,
} from "components/delegation/DelegationDetails";
import { getStatusLabel } from "components/delegation/DelegationCard";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { UserAvatar } from "components/common/CurrentUserAvatar";
import { CurrentUserContext } from "components/context/RequireAuth";
import SimpleRichEditor, {
  Handle as EditorHandle,
} from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import DelegationComment from "components/delegation/DelegationComment";
import { celebrate, getElementCenter } from "common/celebrations";
import {
  useUpdateDelegationTaskComment,
  useDeleteDelegationTaskComment,
} from "services/delegation";
import { useStartExternalConversation } from "services/advisor";
import {
  cleanPhone,
  formatUSPhoneNumber,
  abbreviateTimezone,
} from "common/utils";
import CallInstantiation from "components/activity/CallInstantiation";
import { FeedEntry } from "protogen/feed_pb";
import { useInitiateCall } from "services/phone";
import { CallContext } from "components/context/AddCallContext";
import DropdownMenu from "components/common/DropdownMenu";
import PhoneInputDialog from "components/delegation/PhoneInputDialog";
import { SimpleTimeTracker, TimeEntry } from "components/common/TimeTracking";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { SecretRevealConfirmation } from "components/secrets/SecretRevealConfirmation";
import { Family, FamilyStatus } from "protogen/advisors_service_pb";
import FlexBox from "components/common/FlexBox";
import PhoneCallTaskActions from "components/delegation/PhoneCallTaskActions";
import { extractPhoneFromDelegationDetails } from "components/delegation/phoneCallTaskUtils";
import { isUserAdmin } from "common/userUtils";

export type AccountType = "advisor" | "member";

const CHANGEABLE_STATUSES = [
  "cancelled",
  // "pending",
  "queued",
  // "in_progress",
  // "delegated",
  "completed",
];

const getLocalStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "#4CAF50";
    case "in_progress":
      return "#FF9800";
    case "cancelled":
      return "#C14743";
    default:
      return "#8E9598";
  }
};

const formatDate = (sec: bigint | undefined): string => {
  if (!sec || sec === BigInt(0)) return "-";
  return new Date(Number(sec) * 1000).toLocaleString();
};

const GutterIcon = ({
  icon,
  size = 20,
}: {
  icon?: React.ReactNode;
  size?: number;
}) => (
  <Box
    sx={(theme) => ({
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: size === 16 ? "5px" : "6px",
      border: `1px solid ${theme.palette.border}`,
      borderRadius: "100px",
    })}
  >
    {icon || <Info size={size} color="#198282" />}
  </Box>
);

const FamilyMemberCard = ({
  member,
  accountType,
  onPhoneClick,
}: {
  member: Family["familyMembers"][number];
  accountType: AccountType;
  onPhoneClick: (phone: string) => void;
}) => {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ");
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1px",
        minWidth: "180px",
      }}
    >
      {name && (
        <FlexBox alignItems="center" gap="4px">
          <Typography variant="bodySmall" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          {member.timezone && (
            <Typography
              component="span"
              variant="bodySmall"
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              {" "}
              ({abbreviateTimezone(member.timezone)})
            </Typography>
          )}
        </FlexBox>
      )}
      {member.primaryPhone && (
        <Typography variant="bodySmall">
          {accountType === "advisor" ? (
            <Typography
              component="span"
              variant="bodySmall"
              onClick={() => onPhoneClick(member.primaryPhone)}
              sx={{ color: "primary.main", cursor: "pointer" }}
            >
              {formatUSPhoneNumber(member.primaryPhone)}
            </Typography>
          ) : (
            formatUSPhoneNumber(member.primaryPhone)
          )}
        </Typography>
      )}
      {member.primaryEmail && (
        <Typography
          component="a"
          variant="bodySmall"
          href={`/inbox/email/create?email=${encodeURIComponent(
            member.primaryEmail,
          )}`}
          sx={{
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          {member.primaryEmail}
        </Typography>
      )}
      {member.alternateEmails && member.alternateEmails.length > 0 && (
        <Typography variant="bodySmall" color="text.secondary">
          {member.alternateEmails.join(", ")}
        </Typography>
      )}
    </Box>
  );
};

const CopyableAddress = ({ address }: { address: string }) => {
  const { pushSnack } = useSnackpack();
  const handleCopy = () => {
    navigator.clipboard.writeText(address);
    pushSnack("Address copied to clipboard");
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Typography
        component="a"
        variant="bodySmall"
        href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "text.primary",
          textDecoration: "none",
        }}
      >
        {address}
      </Typography>
      <IconButton size="small" onClick={handleCopy} sx={{ padding: "2px" }}>
        <Copy size={13} />
      </IconButton>
    </Box>
  );
};

const FamilyInfoSection = ({
  familyInfo,
  isMobile,
  accountType,
  onPhoneClick,
}: {
  familyInfo: Family;
  isMobile: boolean;
  accountType: AccountType;
  onPhoneClick: (phone: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const parsedAddress = (() => {
    if (!familyInfo.address) return null;
    try {
      const obj = JSON.parse(familyInfo.address);
      return obj.formattedAddress || familyInfo.address;
    } catch {
      return familyInfo.address;
    }
  })();

  return (
    <Box
      sx={(theme) => ({
        marginBottom: "16px",
        border: `1px solid ${theme.palette.border}`,
        borderRadius: "8px",
        padding: "10px 14px",
      })}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography variant="body" sx={{ fontWeight: 700 }}>
          {familyInfo.name || "Family Info"}
        </Typography>
        <ChevronDown
          size={18}
          style={{
            transition: "transform 0.2s",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>
      <Collapse in={expanded}>
        {parsedAddress && <CopyableAddress address={parsedAddress} />}
        {familyInfo.familyMembers.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "10px" : "32px",
              flexWrap: "wrap",
              marginTop: "8px",
            }}
          >
            {familyInfo.familyMembers.map((member, index) => (
              <FamilyMemberCard
                key={index}
                member={member}
                accountType={accountType}
                onPhoneClick={onPhoneClick}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

type Props = {
  task: DelegationTask | null;
  familyInfo: Family | null;
  loading: boolean;
  updating: boolean;
  accountType: AccountType;
  onStatusChange: (
    status: string,
    comment?: string,
    unassign?: boolean,
    attachments?: PlainMessage<UploadAttachment>[],
    actionDateSec?: number,
    recipientType?: string,
    commentHtml?: string,
  ) => Promise<void>;
  onAddComment: (
    content: string,
    attachments?: PlainMessage<UploadAttachment>[],
    recipientType?: string,
    contentHtml?: string,
  ) => Promise<void>;
  onSaveTimeEntries: (entries: TimeEntry[]) => Promise<void>;
  onRefresh: () => void;
  backPath: string;
  breadcrumbs: { name: string; link?: string; back?: boolean }[];
};

export default ({
  task,
  familyInfo,
  loading,
  updating,
  accountType,
  onStatusChange,
  onAddComment,
  onSaveTimeEntries,
  onRefresh,
  backPath,
  breadcrumbs,
}: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const getAdvisorProfileUrl = useAdvisorProfilePath();
  const [content, setContent] = useState<RichContent | null>(null);
  const [commentKey, setCommentKey] = useState(0);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<EditorHandle>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [uploadsInProgress, setUploadsInProgress] = useState(false);
  const [phoneInputModalOpen, setPhoneInputModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [contactRef, setContactRef] = useState<string | null>(null);
  const [commentRequiredError, setCommentRequiredError] = useState(false);
  const [timeEntryRequiredError, setTimeEntryRequiredError] = useState(false);
  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const timeTrackerRef = useRef<HTMLDivElement>(null);
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [revealSecretRef, setRevealSecretRef] = useState<string | null>(null);
  const [recipientType, setRecipientType] = useState<string>("everyone");
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const isDigitalSubscriptionFamily =
    familyInfo?.status === FamilyStatus.DIGITAL_SUBSCRIPTION;
  const showRecipientTypeDropdown =
    accountType === "advisor" && !isDigitalSubscriptionFamily;
  const commentRecipientType = showRecipientTypeDropdown
    ? recipientType
    : "everyone";

  const { request: updateComment } = useUpdateDelegationTaskComment(() => {
    onRefresh();
  });

  const { request: deleteComment } = useDeleteDelegationTaskComment(() => {
    onRefresh();
  });

  const { request: startExternalConversation } = useStartExternalConversation();
  const { setAcceptNextCall, setActiveCall } = useContext(CallContext);
  const { loading: initiatingCall, request: initiateCall } = useInitiateCall(
    (resp) => {
      setAcceptNextCall();
      setActiveCall(resp.phoneCall);
      handleCallStarted(
        new FeedEntry({
          ref: resp.phoneCall.ref,
          senderRef: resp.phoneCall.callerRef,
          timestampSec: resp.phoneCall.lastUpdatedSec,
          medium: 1, // Medium.PHONE
          phoneCall: resp.phoneCall,
          recipientRefs: resp.phoneCall.recipientRefs,
        }),
      );
    },
  );

  const handleUpdateComment = async (
    ref: string,
    content: string,
    attachments: any[],
    contentHtml?: string,
  ) => {
    await updateComment({
      ref,
      content: contentHtml || content,
      attachments,
      textContent: contentHtml ? content : "",
    });
  };

  const handleDeleteComment = async (ref: string) => {
    await deleteComment({ ref });
  };

  const handleStatusChange = async (
    newStatus: string,
    unassign = false,
    actionDateSec?: number,
  ) => {
    if (!task) return;
    await onStatusChange(
      newStatus,
      content?.text || "",
      unassign,
      content?.attachments,
      actionDateSec,
      commentRecipientType,
      content?.html,
    );
    setContent(null);
    setCommentKey((k) => k + 1);
    setSelectedStatus(null);
    setScheduleForLater(false);
    setScheduledDate(null);
    setScheduledTime(null);
    setRecipientType("everyone");
  };

  const handleDone = async () => {
    await handleStatusChange("completed");
    celebrate(
      doneButtonRef.current
        ? getElementCenter(doneButtonRef.current)
        : undefined,
    );
  };

  const handleStatusSelect = (status: string) => {
    if (status === selectedStatus) {
      setSelectedStatus(null);
    } else {
      setSelectedStatus(status);
    }
  };

  const hasContent = !!(content?.text?.trim() || content?.attachments?.length);

  const handleRightButtonAction = async () => {
    if (selectedStatus === "completed") {
      const timeEntries = task?.timeEntriesJson
        ? JSON.parse(task.timeEntriesJson)
        : [];
      const hasTimeEntries = timeEntries.length > 0;

      if (!hasContent) {
        setCommentRequiredError(true);
        editorRef.current?.focus();
        return;
      }
      if (!hasTimeEntries) {
        setTimeEntryRequiredError(true);
        timeTrackerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
      setCommentRequiredError(false);
      setTimeEntryRequiredError(false);
      await handleDone();
    } else if (selectedStatus === "queued") {
      let actionDateSec: number | undefined = undefined;
      if (scheduleForLater) {
        if (!scheduledDate || !scheduledTime) {
          setScheduleError("Both date and time are required when scheduling");
          return;
        }
        const combinedDateTime = new Date(scheduledDate);
        combinedDateTime.setHours(scheduledTime.getHours());
        combinedDateTime.setMinutes(scheduledTime.getMinutes());
        combinedDateTime.setSeconds(0);
        combinedDateTime.setMilliseconds(0);
        actionDateSec = Math.floor(combinedDateTime.getTime() / 1000);
      }
      setScheduleError(null);
      await handleStatusChange("queued", true, actionDateSec);
      navigate(backPath);
    } else if (selectedStatus === "cancelled") {
      await handleStatusChange("cancelled");
      navigate(backPath);
    } else if (selectedStatus === task?.status && hasContent) {
      await handleAddComment();
      setSelectedStatus(null);
    } else if (selectedStatus) {
      await handleStatusChange(selectedStatus);
    } else if (hasContent) {
      await handleAddComment();
    }
  };

  const getRightButtonText = () => {
    if (selectedStatus === task?.status && hasContent) {
      return "Comment";
    } else if (selectedStatus && hasContent) {
      return "Comment and update status";
    } else if (selectedStatus && !hasContent) {
      return "Update status";
    } else if (hasContent && !selectedStatus) {
      return "Comment";
    }
    return "Comment";
  };

  const isRightButtonDisabled = () => {
    return updating || uploadsInProgress || (!hasContent && !selectedStatus);
  };

  const handleCancel = async () => {
    await handleStatusChange("cancelled");
    navigate(backPath);
  };

  const handleAddComment = async () => {
    if (!task || (!content?.text?.trim() && !content?.attachments?.length))
      return;
    await onAddComment(
      content.text || "",
      content.attachments,
      commentRecipientType,
      content.html,
    );
    setContent(null);
    setCommentKey((k) => k + 1);
    setRecipientType("everyone");
  };

  const detectedPhone = extractPhoneFromDelegationDetails(task?.detailsJson);

  const handleDirectCall = async (phone: string) => {
    if (!task || !phone) return;

    const digits = phone.replace(/\D/g, "");
    const formattedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`;

    const convResp = await startExternalConversation({
      contacts: [
        {
          phoneNumber: formattedPhone,
          displayName: formattedPhone,
          firstName: "",
          lastName: "",
        },
      ],
    });

    if (!convResp?.contacts || convResp.contacts.length === 0) {
      // alert("Failed to create contact");
      return;
    }

    setContactRef(convResp.contacts[0].ref);
    setCallModalOpen(true);
  };

  const handleMakeCall = () => {
    setPhoneInputModalOpen(true);
  };

  const handlePhoneSubmit = async (
    phone: string,
    device: number,
    transcribe: boolean,
  ) => {
    if (!task || !phone.trim()) return;

    const digits = cleanPhone(phone).replace(/\D/g, "");
    if (digits.length < 10) {
      return;
    }

    const formattedPhone = digits.length === 10 ? `+1${digits}` : `+${digits}`;

    const convResp = await startExternalConversation({
      contacts: [
        {
          phoneNumber: formattedPhone,
          displayName: formattedPhone,
          firstName: "",
          lastName: "",
        },
      ],
    });

    if (!convResp?.contacts || convResp.contacts.length === 0) {
      return;
    }

    await initiateCall({
      toMemberRefs: [convResp.contacts[0].ref],
      advisorDevice: device,
      transcriptionEnabled: transcribe,
      delegationTaskRef: task.ref,
    });

    setPhoneInputModalOpen(false);
  };

  const handleCallStarted = (entry: FeedEntry) => {
    setCallModalOpen(false);
    setContactRef(null);
    if (entry.ref) {
      navigate(`/calls/${encodeURIComponent(entry.ref)}`);
    }
  };

  const handleCallModalClose = () => {
    setCallModalOpen(false);
    setContactRef(null);
  };

  const canEditDetails =
    task &&
    (accountType === "member" ||
      isUserAdmin(currentUser) ||
      task.creator?.ref === currentUser.ref ||
      familyInfo?.advisorRef === currentUser.ref);

  useEffect(() => {
    if (searchParams.get("comment") === "1" && !loading && task) {
      setTimeout(() => {
        commentSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        editorRef.current?.focus();
      }, 250);
    }
  }, [searchParams, loading, task]);

  useEffect(() => {
    if (hasContent && commentRequiredError) {
      setCommentRequiredError(false);
    }
  }, [hasContent, commentRequiredError]);

  useEffect(() => {
    const timeEntries = task?.timeEntriesJson
      ? JSON.parse(task.timeEntriesJson)
      : [];
    if (timeEntries.length > 0 && timeEntryRequiredError) {
      setTimeEntryRequiredError(false);
    }
  }, [task?.timeEntriesJson, timeEntryRequiredError]);

  if (loading) {
    return (
      <Box sx={{ padding: "64px" }}>
        <Loading />
      </Box>
    );
  }

  if (!task) {
    return (
      <Box
        sx={{
          margin: isMobile ? "" : "auto",
          maxWidth: "1000px",
          padding: isMobile ? "20px" : "64px",
        }}
      >
        <Typography>Task not found or you don't have access.</Typography>
        <Button
          variant="text"
          onClick={() => navigate(backPath)}
          sx={{ marginTop: "16px" }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const { normal: normalDetails, secret: secretDetails } =
    separateDetailsByType(parseDetails(task.detailsJson));

  const phoneRegex = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g;

  const renderValueWithPhoneLinks = (value: string) => {
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;

    const regex = new RegExp(phoneRegex);
    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      const phone = match[0];
      parts.push(
        <Typography
          key={match.index}
          component="span"
          variant="body"
          onClick={() => handleDirectCall(phone)}
          sx={{
            color: "primary.main",
            cursor: "pointer",
            "&:hover": { textDecoration: "none" },
          }}
        >
          {formatUSPhoneNumber(phone)}
        </Typography>,
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }

    return parts.length > 0 ? parts : value;
  };

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 64px 64px",
      }}
    >
      <Box display="flex" flexDirection="column" marginBottom="24px">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={isMobile ? "start" : "center"}
          gap="16px"
          flexDirection={isMobile ? "column" : "row"}
        >
          <Box display="flex" alignItems="center" gap="12px">
            <Typography variant="display">
              <Zap
                size={24}
                color="#198282"
                fill="#198282"
                style={{ marginRight: "6px", paddingTop: "2px" }}
              />
              {task.title}
            </Typography>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            justifyContent="space-between"
            width={isMobile ? "100%" : "auto"}
          >
            <Chip
              label={
                task.status === "queued" &&
                task.actionDateSec > BigInt(0) &&
                Number(task.actionDateSec) > Date.now() / 1000
                  ? "Scheduled"
                  : getStatusLabel(task.status)
              }
              variant={
                task.status === "completed"
                  ? "success"
                  : task.status === "in_progress"
                    ? "warning"
                    : "gray"
              }
            />
            {isMobile && task.taskRef && (
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={() => {
                  navigate(`/tasks/${encodeURIComponent(task.taskRef)}`);
                }}
                endIcon={<ExternalLink size={14} />}
                sx={{ padding: "0 4px", minWidth: "unset" }}
              >
                View main task
              </Button>
            )}
          </Box>
        </Box>
        {(task.creator || task.taskRef) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              marginTop: "8px",
              flexWrap: "wrap",
            }}
          >
            {task.creator && (
              <Typography variant="body" color="text.secondary">
                Created by{" "}
                {task.creator.isAdvisor ? (
                  <LinkRouter
                    useColorScheme
                    to={getAdvisorProfileUrl(task.creator.ref)}
                    sx={{ display: "inline" }}
                  >
                    {task.creator.displayName}
                  </LinkRouter>
                ) : (
                  <span>{task.creator.displayName}</span>
                )}
                {task.createdSec !== undefined &&
                  task.createdSec !== BigInt(0) && (
                    <>
                      {" "}
                      <DateDisplay
                        date={new Date(Number(task.createdSec) * 1000)}
                        sx={{ display: "inline" }}
                      />
                    </>
                  )}
              </Typography>
            )}
            {!isMobile && task.taskRef && (
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={() => {
                  navigate(`/tasks/${encodeURIComponent(task.taskRef)}`);
                }}
                endIcon={<ExternalLink size={14} />}
                sx={{ padding: "0 4px", minWidth: "unset" }}
              >
                View main task
              </Button>
            )}
          </Box>
        )}
        {task.actionDateSec !== undefined && task.actionDateSec > BigInt(0) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "2px",
            }}
          >
            <FlexBox alignItems="center" gap="4px">
              <CalendarDays size={14} color="#6B6E7B" />
              <Typography variant="body" color="text.secondary">
                <strong>Scheduled</strong>{" "}
                {new Date(Number(task.actionDateSec) * 1000).toLocaleString(
                  undefined,
                  {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  },
                )}
              </Typography>
            </FlexBox>
            {task.status === "queued" &&
              Number(task.actionDateSec) > Date.now() / 1000 && (
                <Typography
                  variant="bodySmall"
                  sx={{
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  This task will not be available for an advisor to pick up
                  until the scheduled time.
                </Typography>
              )}
          </Box>
        )}
        {task.assignee && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginTop: "21px",
            }}
          >
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: "8px",
                backgroundColor: theme.palette.gray[1],
                borderRadius: "12px",
                padding: "8px 20px",
              })}
            >
              <Typography variant="body">Assigned to</Typography>
              <Typography variant="bodyHeavy">
                {task.assignee.displayName}
              </Typography>
            </Box>
            {accountType === "advisor" && familyInfo?.advisorRef && (
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  navigate(
                    `/inbox/messages?recipientRefs=${encodeURIComponent(
                      familyInfo.advisorRef,
                    )}`,
                  );
                }}
                sx={{ alignSelf: "flex-start" }}
              >
                Send a direct message to family's primary advisor
              </Button>
            )}
          </Box>
        )}
      </Box>

      {accountType === "advisor" && task.assignee?.ref === currentUser.ref && (
        <Box
          ref={timeTrackerRef}
          sx={{ display: "flex", gap: isMobile ? "8px" : "16px" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <GutterIcon
              icon={<Clock size={isMobile ? 16 : 20} color="#198282" />}
              size={isMobile ? 16 : 20}
            />
            <Box
              sx={(theme) => ({
                flexGrow: 1,
                borderLeft: `1px solid ${theme.palette.border}`,
              })}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SimpleTimeTracker
              timeEntries={
                task.timeEntriesJson ? JSON.parse(task.timeEntriesJson) : []
              }
              onSave={onSaveTimeEntries}
              showHours={false}
            />
            {timeEntryRequiredError && (
              <Typography
                variant="bodySmall"
                sx={{ color: "error.main", marginTop: "8px" }}
              >
                At least one time entry is required when completing a subtask
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: isMobile ? "8px" : "16px" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <GutterIcon size={isMobile ? 16 : 20} />
          <Box
            sx={(theme) => ({
              flexGrow: 1,
              borderLeft: `1px solid ${theme.palette.border}`,
            })}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            borderRadius: "16px",
            border: "1px solid #D4D4D4",
            padding: "16px 24px",
            marginBottom: "16px",
          }}
        >
          {canEditDetails && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "12px",
              }}
            >
              <Button
                size="small"
                variant="text"
                startIcon={<Pencil size={14} />}
                onClick={() => setEditDetailsOpen(true)}
              >
                Edit details
              </Button>
            </Box>
          )}

          {task.description && (
            <Box sx={{ marginBottom: "16px" }}>
              <Typography variant="h5" sx={{ marginBottom: "4px" }}>
                Instructions
              </Typography>
              <Typography variant="body" sx={{ whiteSpace: "pre-wrap" }}>
                {task.description}
              </Typography>
            </Box>
          )}

          {familyInfo && (
            <FamilyInfoSection
              familyInfo={familyInfo}
              isMobile={isMobile}
              accountType={accountType}
              onPhoneClick={handleDirectCall}
            />
          )}

          {normalDetails.length > 0 && (
            <Box
              sx={{ marginBottom: secretDetails.length > 0 ? "16px" : "16px" }}
            >
              <Typography variant="h5" sx={{ marginBottom: "8px" }}>
                Details
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "12px" : "4px",
                }}
              >
                {normalDetails.map((detail, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? "2px" : "24px",
                    }}
                  >
                    <Typography
                      variant="bodyHeavy"
                      sx={{
                        width: isMobile ? undefined : "200px",
                        minWidth: isMobile ? undefined : "200px",
                        flexShrink: isMobile ? undefined : 0,
                      }}
                    >
                      {detail.name}
                    </Typography>
                    <Typography variant="body" sx={{ whiteSpace: "pre-wrap" }}>
                      {accountType === "advisor"
                        ? renderValueWithPhoneLinks(detail.value)
                        : detail.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {secretDetails.length > 0 && (
            <Box sx={{ marginBottom: "16px" }}>
              <Typography variant="h5" sx={{ marginBottom: "8px" }}>
                Secrets
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "12px" : "4px",
                }}
              >
                {secretDetails.map((detail, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: isMobile ? "2px" : "24px",
                      alignItems: isMobile ? "flex-start" : "center",
                    }}
                  >
                    <Typography
                      variant="bodyHeavy"
                      sx={{
                        width: isMobile ? undefined : "200px",
                        minWidth: isMobile ? undefined : "200px",
                        flexShrink: isMobile ? undefined : 0,
                      }}
                    >
                      {detail.name}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <Typography
                        variant="body"
                        sx={{ color: "text.secondary" }}
                      >
                        ••••••••
                      </Typography>
                      {detail.value && (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<LockKeyhole size={16} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRevealSecretRef(detail.value);
                          }}
                          sx={{
                            minWidth: 0,
                            padding: "2px 6px",
                            color: "primary.main",
                            textTransform: "none",
                            "& .MuiButton-startIcon": {
                              marginRight: "4px",
                            },
                          }}
                        >
                          Reveal
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              gap: "24px",
              flexWrap: "wrap",
              color: "text.secondary",
            }}
          >
            {task.delegatedSec !== undefined &&
              task.delegatedSec !== BigInt(0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Clock size={14} />
                  <Typography variant="bodySmall">
                    Delegated: {formatDate(task.delegatedSec)}
                  </Typography>
                </Box>
              )}
            {task.completedSec !== undefined &&
              task.completedSec !== BigInt(0) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <CheckCircle2 size={14} />
                  <Typography variant="bodySmall">
                    Completed: {formatDate(task.completedSec)}
                  </Typography>
                </Box>
              )}
            {task.timeEstimateMinutes > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  marginBottom: "12px",
                }}
              >
                <Clock size={14} />
                <Typography variant="bodySmall">
                  Est. ~{task.timeEstimateMinutes}m
                </Typography>
              </Box>
            )}
            {task.timeSpentMinutes > 0 && (
              <Typography variant="bodySmall">
                Time spent: {task.timeSpentMinutes}m
              </Typography>
            )}
          </Box>

          <PhoneCallTaskActions
            accountType={accountType}
            serviceType={task.serviceType}
            status={task.status}
            isMobile={isMobile}
            updating={updating}
            detectedPhone={detectedPhone}
            onDirectCall={handleDirectCall}
            onMakeCall={handleMakeCall}
          />
        </Box>
      </Box>

      {task.comments && task.comments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: "8px",
          }}
        >
          {task.comments.map((c, index) => (
            <DelegationComment
              key={c.ref}
              comment={c}
              isLast={index === task.comments.length - 1 && !task.assignee}
              onUpdate={handleUpdateComment}
              onDelete={handleDeleteComment}
              accountType={accountType}
            />
          ))}
        </Box>
      )}

      <Box
        ref={commentSectionRef}
        sx={{
          display: "flex",
          gap: isMobile ? "8px" : "16px",
          marginTop: isMobile ? "16px" : "8px",
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <UserAvatar user={currentUser} size={isMobile ? 26 : 34} />
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <SimpleRichEditor
            ref={editorRef}
            key={commentKey}
            disabled={updating}
            setContent={setContent}
            setUploadsInProgress={setUploadsInProgress}
            placeholder="Comment"
            inputSx={{ borderRadius: "16px" }}
          />
          {commentRequiredError && (
            <Typography
              variant="bodySmall"
              sx={{ color: "error.main", marginTop: "8px" }}
            >
              A quick comment is required when completing a subtask
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            {showRecipientTypeDropdown && (
              <Box sx={{ alignSelf: "start" }}>
                <DropdownMenu
                  variant="text"
                  size="small"
                  dropdownIcon={true}
                  sx={{ alignSelf: "start" }}
                  buttonContent={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                    >
                      <Typography variant="bodySmall">
                        {recipientType === "everyone"
                          ? "Member & Advisors"
                          : "Advisors only"}
                      </Typography>
                    </Box>
                  }
                  options={[
                    {
                      title: "Member & Advisors",
                      onClick: () => setRecipientType("everyone"),
                    },
                    {
                      title: "Advisors only",
                      onClick: () => setRecipientType("internal-advisor"),
                    },
                  ]}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: "12px" }}>
              {accountType === "advisor" &&
                task.assignee?.ref === currentUser.ref && (
                  <>
                    <DropdownMenu
                      variant="outlined"
                      size="small"
                      dropdownIcon={true}
                      buttonContent={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            color: "#198282",
                          }}
                        >
                          {selectedStatus && (
                            <Box
                              sx={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor:
                                  getLocalStatusColor(selectedStatus),
                              }}
                            />
                          )}
                          {selectedStatus
                            ? selectedStatus === "queued"
                              ? "Re-queue"
                              : selectedStatus === "completed"
                                ? "Done"
                                : getStatusLabel(selectedStatus)
                            : "Update status"}
                        </Box>
                      }
                      options={CHANGEABLE_STATUSES.map((status) => ({
                        key: status,
                        title: (
                          <>
                            <Typography variant="body">
                              {status === "queued"
                                ? "Re-queue"
                                : status === "completed"
                                  ? "Done"
                                  : getStatusLabel(status)}
                            </Typography>
                            {status === task.status && (
                              <Typography
                                variant="bodySmall"
                                color="text.tertiary"
                              >
                                {selectedStatus ? "(Reset)" : "(Current)"}
                              </Typography>
                            )}
                          </>
                        ),
                        startIcon: (
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: getLocalStatusColor(status),
                            }}
                          />
                        ),
                        onClick: () => handleStatusSelect(status),
                      }))}
                      sx={{
                        minWidth: "140px",
                        justifyContent: "space-between",
                        fontWeight: "500",
                      }}
                    />
                    {selectedStatus === "queued" && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                          padding: "12px",
                          border: "1px solid #D4D4D4",
                          borderRadius: "8px",
                          flex: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={scheduleForLater}
                              onChange={(e) =>
                                setScheduleForLater(e.target.checked)
                              }
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="bodySmall">
                              Schedule for later
                            </Typography>
                          }
                        />
                        <Collapse in={scheduleForLater}>
                          <Box
                            sx={{
                              display: "flex",
                              gap: "12px",
                              flexWrap: "wrap",
                            }}
                          >
                            <FormControl sx={{ flex: 1, minWidth: "140px" }}>
                              <DatePicker
                                value={scheduledDate}
                                sx={{
                                  width: "100%",
                                  ".MuiButtonBase-root": {
                                    marginRight: "unset",
                                  },
                                }}
                                onChange={(newValue) => {
                                  setScheduledDate(newValue);
                                  setScheduleError(null);
                                }}
                                format="MM/dd/yyyy"
                                open={datePickerOpen}
                                onClose={() => setDatePickerOpen(false)}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    onClick: (e) => {
                                      e.stopPropagation();
                                      setDatePickerOpen(true);
                                      setTimeout(() => {
                                        (e.target as HTMLInputElement).focus();
                                      });
                                    },
                                    InputProps: {
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            onClick={() =>
                                              setDatePickerOpen(true)
                                            }
                                            edge="end"
                                          >
                                            <CalendarDays size={20} />
                                          </IconButton>
                                        </InputAdornment>
                                      ),
                                    },
                                  },
                                }}
                              />
                            </FormControl>
                            <DesktopTimePicker
                              value={scheduledTime}
                              sx={{
                                flex: 1,
                                minWidth: "120px",
                                ".MuiButtonBase-root": { marginRight: "unset" },
                                "& .MuiInputBase-input": { marginTop: "0px" },
                              }}
                              onChange={(newValue) => {
                                setScheduledTime(newValue);
                                setScheduleError(null);
                              }}
                              open={timePickerOpen}
                              onClose={() => setTimePickerOpen(false)}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    setTimePickerOpen(!timePickerOpen);
                                    setTimeout(() => {
                                      (e.target as HTMLInputElement).focus();
                                    });
                                  },
                                  InputProps: {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() =>
                                            setTimePickerOpen(!timePickerOpen)
                                          }
                                          edge="end"
                                        >
                                          <Clock size={20} />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Collapse>
                        {scheduleError && (
                          <Typography
                            variant="bodySmall"
                            sx={{ color: "error.main" }}
                          >
                            {scheduleError}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Button
                      ref={doneButtonRef}
                      size="small"
                      onClick={handleRightButtonAction}
                      disabled={isRightButtonDisabled()}
                    >
                      {getRightButtonText()}
                    </Button>
                  </>
                )}
              {(accountType === "member" ||
                (accountType === "advisor" &&
                  task?.assignee?.ref !== currentUser.ref)) && (
                <>
                  <Button
                    size="small"
                    onClick={handleAddComment}
                    disabled={
                      updating ||
                      uploadsInProgress ||
                      (!content?.text?.trim() && !content?.attachments?.length)
                    }
                  >
                    Comment
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleCancel}
                    disabled={
                      updating ||
                      task.status === "cancelled" ||
                      task.status === "completed"
                    }
                    sx={{
                      "&:hover": {
                        backgroundColor: "error.main",
                        color: "error.contrastText",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <PhoneInputDialog
        open={phoneInputModalOpen}
        onClose={() => setPhoneInputModalOpen(false)}
        onSubmit={handlePhoneSubmit}
        loading={initiatingCall}
        isMobile={isMobile}
        phoneTeaser={currentUser.phoneTeaser}
      />

      {/* Call Instantiation Dialog */}
      <CallInstantiation
        onStarted={handleCallStarted}
        recipientRef={contactRef || undefined}
        closed={!callModalOpen}
        onClose={handleCallModalClose}
        delegationTaskRef={task?.ref}
        callingPhoneNumber={detectedPhone || undefined}
      />

      {revealSecretRef && (
        <SecretRevealConfirmation
          open={!!revealSecretRef}
          onClose={() => setRevealSecretRef(null)}
          secretRef={revealSecretRef}
        />
      )}

      {task && (
        <EditDelegationDetailsDialog
          open={editDetailsOpen}
          task={task}
          familyRef={familyInfo?.ref}
          onClose={() => setEditDetailsOpen(false)}
          onSaved={onRefresh}
        />
      )}
    </Box>
  );
};
